import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AssistantFeedback,
  AssistantToolActivity,
  AssistantTurnOutcome,
  AssistantUiAction,
} from '@/api/assistant/assistantApi.types';
import { Icon } from '@/components/common/Icon';
import { AssistantActionChips } from './AssistantActionChips';
import { AssistantActivityList } from './AssistantActivityList';
import { AssistantFeedbackButtons } from './AssistantFeedbackButtons';
import { AssistantFormattedText } from './AssistantFormattedText';

interface AssistantReplyProps {
  messageId: string | null;
  text: string;
  activities: AssistantToolActivity[];
  actions: AssistantUiAction[];
  isWaiting: boolean;
  isStreaming: boolean;
  outcome: AssistantTurnOutcome | null;
  errorCode: string | null;
  errorMessage: string;
  feedback: AssistantFeedback | null;
  actionNoticeKey: string | null;
}

function resolveNoticeKey(
  outcome: AssistantTurnOutcome | null,
  errorCode: string | null
): string | null {
  if (errorCode !== null) {
    return 'errors.' + errorCode.replace('assistant.', '');
  }
  if (outcome === 'refused') {
    return 'notices.refused';
  }
  if (outcome === 'incomplete') {
    return 'notices.incomplete';
  }
  if (outcome === 'cancelled') {
    return 'notices.stopped';
  }
  return null;
}

function AssistantReplyContent({
  messageId,
  text,
  activities,
  actions,
  isWaiting,
  isStreaming,
  outcome,
  errorCode,
  errorMessage,
  feedback,
  actionNoticeKey,
}: AssistantReplyProps) {
  const { t } = useTranslation('assistant');
  const noticeKey = resolveNoticeKey(outcome, errorCode);
  const isFinished = isWaiting === false && isStreaming === false;
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-7 h-7 rounded-lg bg-zooprimary text-zoowhite flex items-center justify-center shrink-0 mt-0.5 text-sm">
        <Icon name="Sparkles" />
      </div>
      <div className="flex-1 min-w-0">
        <AssistantActivityList activities={activities} />
        {isWaiting && text.length === 0 && activities.length === 0 ? (
          <p className="text-xs text-zoomuted py-1 motion-safe:animate-pulse">{t('thinking')}</p>
        ) : null}
        {text.length > 0 ? <AssistantFormattedText text={text} isStreaming={isStreaming} /> : null}
        {errorMessage.length > 0 ? (
          <p className="mt-2 text-xs text-zooerrortext flex items-start gap-1.5">
            <Icon name="AlertCircle" className="shrink-0 mt-0.5" />
            {errorMessage}
          </p>
        ) : null}
        {noticeKey !== null ? (
          <p className="mt-2 text-xs text-zoomuted flex items-start gap-1.5">
            <Icon name="Info" className="shrink-0 mt-0.5" />
            {t(noticeKey, { defaultValue: t('errors.generic') })}
          </p>
        ) : null}
        {isFinished && actionNoticeKey !== null ? (
          <p className="mt-3 text-xs text-zoowarningtext flex items-start gap-1.5" role="status">
            <Icon name="Info" className="shrink-0 mt-0.5" />
            {t(actionNoticeKey)}
          </p>
        ) : null}
        {isFinished ? <AssistantActionChips actions={actions} messageId={messageId} /> : null}
        {isFinished && messageId !== null && errorCode === null && text.length > 0 ? (
          <AssistantFeedbackButtons messageId={messageId} feedback={feedback} />
        ) : null}
      </div>
    </div>
  );
}

export const AssistantReply = memo(AssistantReplyContent);
